/**
 * FilterPanel Component
 *
 * Two-column layout: left category nav, right content. Token-only styling for light/dark.
 */

import React, { useState, useMemo, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { X, Filter } from 'lucide-react'
import { FilterState, SearchStates } from './types'
import { FilterPanelProps } from './types'

type FilterCategory = 'basic' | 'dates' | 'progress' | 'advanced' | 'udf'

const CATEGORIES: { key: FilterCategory; label: string }[] = [
  { key: 'basic', label: 'Basic' },
  { key: 'dates', label: 'Dates' },
  { key: 'progress', label: 'Progress' },
  { key: 'advanced', label: 'Advanced' },
  { key: 'udf', label: 'UDF' },
]

export default function FilterPanel({
  filters,
  onFiltersChange,
  searchStates,
  onSearchStatesChange,
  onClose,
  activities,
  udfLabels = {},
}: FilterPanelProps) {
  const [localFilters, setLocalFilters] = useState<FilterState>(filters)
  const [localSearchStates, setLocalSearchStates] = useState<SearchStates>(searchStates)
  const [activeCategory, setActiveCategory] = useState<FilterCategory>('basic')

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const filterOptions = useMemo(() => {
    const wbs = [...new Set(activities.map((a) => a.wbs_name).filter(Boolean))]
    const calendars = [...new Set(activities.map((a) => a.calendar_name).filter(Boolean))]
    const types = [...new Set(activities.map((a) => a.type).filter(Boolean))]
    const statuses = [...new Set(activities.map((a) => a.status).filter(Boolean))]
    const udfValuesByField = new Map<string, Set<string>>()
    activities.forEach((activity) => {
      Object.keys(activity).forEach((key) => {
        const keyLower = key.toLowerCase()
        if (
          (keyLower.startsWith('udf_') || (keyLower.startsWith('udf') && keyLower !== 'udf_values')) &&
          keyLower !== 'udf_values'
        ) {
          const value = (activity as any)[key]
          if (value && typeof value === 'string' && value.trim()) {
            if (!udfValuesByField.has(key)) udfValuesByField.set(key, new Set())
            udfValuesByField.get(key)!.add(value)
          }
        }
      })
    })
    const allUdfValues = new Set<string>()
    udfValuesByField.forEach((values) => values.forEach((v) => allUdfValues.add(v)))
    return { wbs, calendars, types, statuses, udf: Array.from(allUdfValues) }
  }, [activities])

  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }))
  }

  const handleSearchChange = (key: keyof SearchStates, value: string) => {
    setLocalSearchStates((prev) => ({ ...prev, [key]: value }))
  }

  const handleToggleArrayFilter = (key: keyof FilterState, value: string) => {
    setLocalFilters((prev) => {
      const currentArray = prev[key] as string[]
      const newArray = currentArray.includes(value)
        ? currentArray.filter((item) => item !== value)
        : [...currentArray, value]
      return { ...prev, [key]: newArray }
    })
  }

  const handleApply = () => {
    onFiltersChange(localFilters)
    onSearchStatesChange(localSearchStates)
    onClose()
  }

  const handleReset = () => {
    setLocalFilters({
      dateRange: { start: '', end: '' },
      resources: [],
      wbs: [],
      calendars: [],
      types: [],
      statuses: [],
      udf: [],
      criticalOnly: false,
    })
    setLocalSearchStates({
      wbs: '',
      calendars: '',
      types: '',
      statuses: '',
      udf: '',
    })
  }

  const getFilteredOptions = (options: string[], searchTerm: string) => {
    return options.filter((o) => o.toLowerCase().includes(searchTerm.toLowerCase()))
  }

  const blockClass = 'rounded-lg border border-border bg-card p-4 space-y-3'
  const blockTitleClass = 'text-xs font-medium uppercase tracking-wider text-muted-foreground'
  const optionLabelClass = 'flex items-center gap-2.5 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent/50 transition-colors'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-border bg-muted/50 px-6 py-3">
          <h2 className="flex items-center gap-2.5 text-base font-display font-semibold text-foreground">
            <span className="rounded-md bg-primary/10 p-1.5">
              <Filter className="h-4 w-4 text-primary" />
            </span>
            Advanced Filters
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body: two-column */}
        <div className="flex min-h-0 flex-1">
          {/* Left: category nav */}
          <nav className="w-[180px] flex-shrink-0 border-r border-border bg-muted/30 py-3">
            <ul className="space-y-0.5 px-2">
              {CATEGORIES.map(({ key, label }) => (
                <li key={key}>
                  <button
                    type="button"
                    onClick={() => setActiveCategory(key)}
                    className={`w-full rounded-md px-3 py-2 text-left text-sm font-medium transition-colors ${
                      activeCategory === key
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    }`}
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Right: content for active category */}
          <div className="min-w-0 flex-1 overflow-y-auto p-5">
            {activeCategory === 'basic' && (
              <div className="space-y-4">
                <div className={`flex items-center gap-2.5 ${blockClass}`}>
                  <Checkbox
                    id="critical-only"
                    checked={localFilters.criticalOnly}
                    onCheckedChange={(checked) => handleFilterChange('criticalOnly', checked)}
                  />
                  <label htmlFor="critical-only" className="text-sm font-medium text-foreground cursor-pointer">
                    Critical activities only
                  </label>
                </div>

                <div className={blockClass}>
                  <h4 className={blockTitleClass}>WBS</h4>
                  <Input
                    placeholder="Search WBS..."
                    value={localSearchStates.wbs}
                    onChange={(e) => handleSearchChange('wbs', e.target.value)}
                    className="h-8 text-sm"
                  />
                  <div className="max-h-48 space-y-0.5 overflow-y-auto pt-1">
                    {getFilteredOptions(filterOptions.wbs, localSearchStates.wbs).map((wbs) => (
                      <label key={wbs} className={optionLabelClass}>
                        <Checkbox
                          checked={localFilters.wbs.includes(wbs)}
                          onCheckedChange={() => handleToggleArrayFilter('wbs', wbs)}
                        />
                        <span className="text-sm text-foreground">{wbs}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className={blockClass}>
                  <h4 className={blockTitleClass}>Calendar</h4>
                  <Input
                    placeholder="Search calendars..."
                    value={localSearchStates.calendars}
                    onChange={(e) => handleSearchChange('calendars', e.target.value)}
                    className="h-8 text-sm"
                  />
                  <div className="max-h-48 space-y-0.5 overflow-y-auto pt-1">
                    {getFilteredOptions(filterOptions.calendars, localSearchStates.calendars).map((calendar) => (
                      <label key={calendar} className={optionLabelClass}>
                        <Checkbox
                          checked={localFilters.calendars.includes(calendar)}
                          onCheckedChange={() => handleToggleArrayFilter('calendars', calendar)}
                        />
                        <span className="text-sm text-foreground">{calendar}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className={blockClass}>
                  <h4 className={blockTitleClass}>Type</h4>
                  <Input
                    placeholder="Search types..."
                    value={localSearchStates.types}
                    onChange={(e) => handleSearchChange('types', e.target.value)}
                    className="h-8 text-sm"
                  />
                  <div className="max-h-48 space-y-0.5 overflow-y-auto pt-1">
                    {getFilteredOptions(filterOptions.types, localSearchStates.types).map((type) => (
                      <label key={type} className={optionLabelClass}>
                        <Checkbox
                          checked={localFilters.types.includes(type)}
                          onCheckedChange={() => handleToggleArrayFilter('types', type)}
                        />
                        <span className="text-sm text-foreground">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className={blockClass}>
                  <h4 className={blockTitleClass}>Status</h4>
                  <Input
                    placeholder="Search statuses..."
                    value={localSearchStates.statuses}
                    onChange={(e) => handleSearchChange('statuses', e.target.value)}
                    className="h-8 text-sm"
                  />
                  <div className="max-h-48 space-y-0.5 overflow-y-auto pt-1">
                    {getFilteredOptions(filterOptions.statuses, localSearchStates.statuses).map((status) => (
                      <label key={status} className={optionLabelClass}>
                        <Checkbox
                          checked={localFilters.statuses.includes(status)}
                          onCheckedChange={() => handleToggleArrayFilter('statuses', status)}
                        />
                        <span className="text-sm text-foreground">{status}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeCategory === 'dates' && (
              <div className={blockClass}>
                <h4 className={blockTitleClass}>Date Range</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Start date</label>
                    <Input
                      type="date"
                      value={localFilters.dateRange.start}
                      onChange={(e) =>
                        handleFilterChange('dateRange', { ...localFilters.dateRange, start: e.target.value })
                      }
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">End date</label>
                    <Input
                      type="date"
                      value={localFilters.dateRange.end}
                      onChange={(e) =>
                        handleFilterChange('dateRange', { ...localFilters.dateRange, end: e.target.value })
                      }
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeCategory === 'progress' && (
              <div className="py-12 text-center text-sm text-muted-foreground">Progress filters coming soon</div>
            )}

            {activeCategory === 'advanced' && (
              <div className="py-12 text-center text-sm text-muted-foreground">Advanced filters coming soon</div>
            )}

            {activeCategory === 'udf' && (() => {
              const udfFieldsWithValues = new Map<string, { label: string; values: string[] }>()
              activities.forEach((activity) => {
                Object.keys(activity).forEach((key) => {
                  const keyLower = key.toLowerCase()
                  if (
                    (keyLower.startsWith('udf_') || (keyLower.startsWith('udf') && keyLower !== 'udf_values')) &&
                    keyLower !== 'udf_values'
                  ) {
                    const value = (activity as any)[key]
                    if (value && typeof value === 'string' && value.trim()) {
                      if (!udfFieldsWithValues.has(key)) {
                        const udfTypeId = key.replace(/^udf_?/i, '')
                        const fieldLabel =
                          udfLabels?.[udfTypeId] ? `${udfLabels[udfTypeId]} (UDF)` : udfTypeId ? `UDF ${udfTypeId}` : key
                        udfFieldsWithValues.set(key, { label: fieldLabel, values: [] })
                      }
                      const field = udfFieldsWithValues.get(key)!
                      if (!field.values.includes(value)) field.values.push(value)
                    }
                  }
                })
              })
              const sorted = Array.from(udfFieldsWithValues.entries()).sort((a, b) =>
                a[1].label.localeCompare(b[1].label)
              )
              if (sorted.length === 0) {
                return <div className="py-12 text-center text-sm text-muted-foreground">No UDF fields found</div>
              }
              return (
                <div className="space-y-4">
                  {sorted.map(([fieldKey, fieldData]) => (
                    <div key={fieldKey} className={blockClass}>
                      <h4 className={blockTitleClass}>{fieldData.label}</h4>
                      <Input
                        placeholder={`Search ${fieldData.label}...`}
                        value={localSearchStates.udf}
                        onChange={(e) => handleSearchChange('udf', e.target.value)}
                        className="h-8 text-sm"
                      />
                      <div className="max-h-48 space-y-0.5 overflow-y-auto pt-1">
                        {getFilteredOptions(fieldData.values, localSearchStates.udf).map((udfValue) => (
                          <label key={`${fieldKey}-${udfValue}`} className={optionLabelClass}>
                            <Checkbox
                              checked={localFilters.udf.includes(udfValue)}
                              onCheckedChange={() => handleToggleArrayFilter('udf', udfValue)}
                            />
                            <span className="text-sm text-foreground">{udfValue}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )
            })()}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 justify-end gap-2 border-t border-border bg-muted/50 px-6 py-3">
          <Button variant="ghost" size="sm" onClick={handleReset} className="text-muted-foreground mr-auto">
            Reset All
          </Button>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleApply}>
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  )
}
